import _                                               from 'underscore';
import React, { FC, lazy, useState }                   from 'react';
import { Redirect, Switch, useHistory, useRouteMatch } from 'react-router-dom';

import { RouterUtility }                                                   from '@src/util';
import { ProjectAccessControl, RoleAccessControl, RoleAccessControlProps } from '@src/views/components/util/access/RoleAccessControl';
import { PrivateRoute }                                                    from '@src/views/components/util/routing/PrivateRoute';

import { useCurrentUser, usePrefStates, useSuspense }         from '@src/hooks';
import { CorvalBusinessFunction, RouterLocationParams, Uuid } from '@src/types';
import { ITermDictionary }                                    from '@src/lang/LangTypes';
import { RolePolicyType }                                     from '@src/services/RoleAccessService';
import { PageMeta }                                           from '@src/views/containers/PageMeta';
import { ProjectConfig, ProjectContextConfigType }            from './ProjectConfig';
import { LaunchDarklyIdentifier }                             from './LaunchDarklyIdentifier';
import { ENVIRONMENT_IS_DEVELOPMENT }                         from "@src/constants";


const ProjectManagementPage = lazy( () => import('./management/ProjectManagementPage') );
const ProjectSurveyPage = lazy( () => import('./survey/ProjectSurveyPage') );
const ProjectPlanningPage = lazy( () => import('./project-planning/ProjectPlanningPage') );
const ProjectObjectivesPage = lazy( () => import('./objectives/ProjectObjectivesPage') );
const ProjectMappingsPage = lazy( () => import('./mapping/ProjectMappingsPage') );
const ProjectResourcePlanningPage = lazy( () => import('./resource-planning/ProjectResourcePlanningPage') );
const ProjectToolsHubPage = lazy( () => import('./tools/ProjectToolsHubPage') );
const DashboardPage = lazy( () => import('./dash/DashContainerPage') );

interface ProjectContextType {
    projectId: Uuid,
    organizationId: Uuid,
    projectVersionId?: Uuid,
    projectName?: string,
    projectUtilSettings?: object,
    projectBusinessFunctions?: {
        all: CorvalBusinessFunction[],
        active: CorvalBusinessFunction[],
        archived: CorvalBusinessFunction[],
    },
    projectDictionary?: ITermDictionary,
    isCorvalManaged?: boolean,
    isDiscoveryComplete?: boolean,
    isTemplateSource?: boolean,
    setDiscoveryComplete?: () => void;
    setProjectVersion?: ( versionId: Uuid ) => void;
}

const routeTo = RouterUtility.getRoutePath;

const routePaths = {
    noAccess:         `${ routeTo( 'HOME' ) }`,
    dashboard:        `${ routeTo( 'DASHBOARD' ) }`,
    manage:           `${ routeTo( 'PROJECT.MANAGE' ) }`,
    surveys:          `${ routeTo( 'PROJECT.SURVEYS' ) }/:surveyType?/:surveyTypeId?`,
    objectives:       `${ routeTo( 'PROJECT.OBJECTIVES' ) }`,
    roadmap:          `${ routeTo( 'PROJECT.OBJECTIVES.ROADMAP' ) }`,
    assumptions:      `${ routeTo( 'PROJECT.ASSUMPTIONS' ) }`,
    projectPlanning:  `${ routeTo( 'PROJECT.PLANNING' ) }`,
    resourcePlanning: `${ routeTo( 'PROJECT.RESOURCE_PLANNING' ) }`,
    map:              `${ routeTo( 'PROJECT.MAP' ) }/:section?`,
    tools:            `${ routeTo( 'PROJECT.TOOLS' ) }`,
    dash:             `${ routeTo( 'PROJECT.DASH' ) }`
};

const ProjectContext: React.Context<ProjectContextType>                       = React.createContext( {
          projectId:                null,
          organizationId:           null,
          projectVersionId:         null,
          projectName:              null,
          projectUtilSettings:      {},
          projectBusinessFunctions: {
              all:      [],
              active:   [],
              archived: [],
          },
          projectDictionary:        null,
          isCorvalManaged:          false,
          isTemplateSource:         false,
          isDiscoveryComplete:      true,
          setDiscoveryComplete:     () => {},
          setProjectVersion:        ( versionId ) => {}
      } ),
      ProjectContextValues: React.MutableRefObject<ProjectContextType | null> = React.createRef<ProjectContextType>();

const ProjectIndexPage: FC = () => {

    let history                             = useHistory(),
        match                               = useRouteMatch<RouterLocationParams.ProjectSpecificPage>(),
        { withSuspense }                    = useSuspense(),
        [ projectConfig, setProjectConfig ] = useState<ProjectContextConfigType>( {} ),
        [ , prefs, prefEnums ]              = usePrefStates();

    let { currentUser } = useCurrentUser();

    // the project ID being requested in the URL
    let currentProjectId = match.params?.projectId ?? null;

    // the specific project prefs for this ID being requested in the URL
    const [ , projectSpecificPrefs ] = usePrefStates( currentProjectId );

    // the project version updates based on the dropdown change
    const setDiscoveryComplete = () => {

        setProjectConfig( {
            ...projectConfig,
            isDiscoveryComplete: true
        } );
    };

    // the project version updates based on the dropdown change
    const setProjectVersion = ( versionId ) => {

        setProjectConfig( {
            ...projectConfig,
            projectVersionId: versionId
        } );

        // update the project version ID in the prefs
        if( currentProjectId ){

            if( ENVIRONMENT_IS_DEVELOPMENT ){
                console.log( {
                    projectId:        currentProjectId,
                    projectVersionId: versionId
                } );
            }

            projectSpecificPrefs.update( prefEnums.PROJECT_VERSION_ID, versionId );
        }
    };


    // get the specific project from the current user account
    let memberProject = _.findWhere( currentUser.projects, { projectId: currentProjectId } ) ?? null;

    // no project id given, or no project found on account
    if( !currentProjectId || !memberProject ){
        history.replace( routePaths.dashboard );
    }

    // set the organization from the project
    let currentOrganizationId = memberProject?.project?.organizationId ?? null;

    // no org id found
    if( !currentOrganizationId ){
        history.replace( routePaths.dashboard );
    }

    // Set up navigation denial handler
    const handleNavigationDenied = () => {

        // remove last project section
        // note: same as clearRootSection from useProject but
        // we cannot use that on this view due to routing and
        // context issues.
        prefs.update( null, {
            [ prefEnums.PROJECT_SECTION ]: null,
        } );
    };

    // set the project name
    let currentProjectName = memberProject?.project?.name ?? null;

    // set up role access control props w/ project's context object,
    // so we don't need to keep specifying project / org,
    // otherwise we'd need to specify id's on each control
    let accessProps: RoleAccessControlProps = {
        context:  ProjectContext as unknown as ProjectContextType,
        redirect: routePaths.dashboard,
        onDeny:   handleNavigationDenied
    };

    // Set context and config values (if available)
    ProjectContextValues.current = {
        projectId:      currentProjectId,
        projectName:    currentProjectName,
        organizationId: currentOrganizationId,
        setProjectVersion,
        setDiscoveryComplete,
        ...projectConfig,
    };

    // Update config state when it arrives
    const handleConfigUpdated = ( updatedConfig: ProjectContextConfigType ) => {

        if( _.isObject( updatedConfig ) ){

            setProjectConfig( updatedConfig );

            if( currentProjectId && !projectConfig[ 'isCorvalManaged' ] && updatedConfig.projectVersionId ){

                if( ENVIRONMENT_IS_DEVELOPMENT ){
                    console.log( 'Config Update', {
                        projectId:        currentProjectId,
                        projectVersionId: updatedConfig.projectVersionId
                    } );
                }

                projectSpecificPrefs.update( prefEnums.PROJECT_VERSION_ID, updatedConfig.projectVersionId );

            }
        }
    };

    const redirects = {
        projectPlanning: RouterUtility.placeParamsInPath( routePaths.projectPlanning, { projectId: currentProjectId } ),
        //dash: RouterUtility.placeParamsInPath(routePaths.dash, { projectId: currentProjectId }),
        roadmap: RouterUtility.placeParamsInPath( routePaths.roadmap, { projectId: currentProjectId } ),
    };

    return (

        <ProjectContext.Provider value={ ProjectContextValues.current }>

            <ProjectConfig key={ `${ currentProjectId }-${ projectConfig.projectVersionId ?? 'managed' }` } onConfigUpdated={ handleConfigUpdated }>

                <PageMeta title={ 'Project' }>

                    <ProjectAccessControl policies={ [ RolePolicyType.PROJECT_ACCESS ] } { ...accessProps } redirect={ routePaths.noAccess }>

                        <LaunchDarklyIdentifier>

                            <Switch>

                                {/* Redirect to Project Planning Area */ }
                                <Redirect from={ routePaths.assumptions } to={ routePaths.projectPlanning }/>

                                <PrivateRoute path={ routePaths.manage } exact={ false } component={ ( props: any ) => (
                                    <ProjectAccessControl policies={ [ RolePolicyType.PROJECT_MANAGE_ACCESS ] } { ...accessProps } redirect={ redirects.projectPlanning }>
                                        { withSuspense( ProjectManagementPage )( props ) }
                                    </ProjectAccessControl>
                                ) }/>

                                <PrivateRoute path={ routePaths.surveys } exact={ true } component={ ( props: any ) => (
                                    <ProjectAccessControl policies={ [ RolePolicyType.PROJECT_SURVEY_VIEW ] } { ...accessProps }>
                                        { withSuspense( ProjectSurveyPage )( props ) }
                                    </ProjectAccessControl>
                                ) }/>

                                {/* DEFAULT PAGE */ }
                                <PrivateRoute path={ routePaths.projectPlanning } exact={ false } component={ ( props: any ) => (
                                    <RoleAccessControl featureFlag={ 'permissionProjectPlanningView' } { ...accessProps } redirect={ redirects.roadmap }>
                                        { withSuspense( ProjectPlanningPage )( props ) }
                                    </RoleAccessControl>
                                ) }/>

                                <PrivateRoute path={ routePaths.objectives } exact={ false } component={ ( props: any ) => (
                                    <ProjectAccessControl policies={ [ RolePolicyType.PROJECT_OBJECTIVES_VIEW ] } { ...accessProps }>
                                        { withSuspense( ProjectObjectivesPage )( props ) }
                                    </ProjectAccessControl>
                                ) }/>

                                <PrivateRoute path={ routePaths.resourcePlanning } exact={ false } component={ ( props: any ) => (
                                    <RoleAccessControl featureFlag={ 'permissionProjectResourcePlanningView' } { ...accessProps } redirect={ redirects.projectPlanning }>
                                        { withSuspense( ProjectResourcePlanningPage, { inverted: true } )( props ) }
                                    </RoleAccessControl>
                                ) }/>

                                <PrivateRoute path={ routePaths.map } exact={ false } component={ ( props: any ) => (
                                    <RoleAccessControl
                                        featureFlag={ [ 'permissionProjectActivityMapViewGantt', 'permissionProjectActivityMapViewGrid' ] } featureFlagPassValue={ true }{ ...accessProps } redirect={ redirects.roadmap }>
                                        { withSuspense( ProjectMappingsPage, { inverted: true } )( props ) }
                                    </RoleAccessControl>
                                ) }/>

                                <PrivateRoute path={ routePaths.tools } exact={ false } component={ ( props: any ) => (
                                    <RoleAccessControl featureFlag={ 'permissionProjectPlanningView' } { ...accessProps } redirect={ redirects.projectPlanning }>
                                        { withSuspense( ProjectToolsHubPage, { inverted: true } )( props ) }
                                    </RoleAccessControl>
                                ) }/>
                                <PrivateRoute path={ routePaths.dash } exact={ false } component={ ( props: any ) => (
                                    <RoleAccessControl featureFlag={ 'permissionProjectPlanningView' } { ...accessProps } redirect={ redirects.roadmap }>
                                        { withSuspense( DashboardPage, { inverted: true } )( props ) }
                                    </RoleAccessControl>
                                ) }/>


                            </Switch>

                        </LaunchDarklyIdentifier>

                    </ProjectAccessControl>

                </PageMeta>

            </ProjectConfig>

        </ProjectContext.Provider>

    );
};

//@ts-ignore
// ProjectIndexPage.whyDidYouRender = false;

export { ProjectContext, ProjectContextType };

export default ProjectIndexPage;
